import { useAppContextController } from "context/AppContext";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// @mui material components
import {
  Autocomplete,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";

// Material Dashboard 2 PRO React components
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomCard from "components/CustomCard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// NewProduct page components
import {
  AttachFile,
  Description,
  Diversity3,
  FolderCopy,
  Info,
  Timeline,
  Work,
} from "@mui/icons-material";
import { makeStyles, styled } from "@mui/styles";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import fetchCompanyJob from "api/companies/fetchCompanyJob";
import fetchCustomers from "api/companies/fetchCustomers";
import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import { getCustomFieldByFeature } from "api/customFields";
import AddToCalendarButton from "components/AddToCalendarButton";
import CompanyLogoSquare from "components/CompanyLogoSquare";
import CustomTextField from "components/Form/CustomTextField";
import SplitPanelSelector from "components/SplitPanelSelector";
import FlexWrapper from "components/Wrapper/FlexWrapper";
import { interviewInfoSchema } from "data/interview";
import useDropdowns from "hooks/useDropdowns";
import useSessionAuth from "hooks/useSessionAuth";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import FormField from "layouts/applications/wizard/components/FormField";
import createEvent from "layouts/pages/events/actions/createEvent";
import fetchEvents from "layouts/pages/events/actions/fetchEvents";
import fetchSingleEvent from "layouts/pages/events/actions/fetchSingleEvent";
import updateEvent from "layouts/pages/events/actions/updateEvent";
import SelectHiringManager from "layouts/pages/events/components/InterviewEditor/SelectHiringManager";
import SelectInterviewLocation from "layouts/pages/events/components/InterviewEditor/SelectInterviewLocation";
import fetchUsers from "layouts/pages/profile/actions/fetchUsers";
import { isEmpty, isEqual } from "lodash";
import { useSnackbar } from "notistack";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";
import generateSlug from "utils/generateSlug";
import { getDirtyFields } from "utils/helpers/formHelpers";
import InterviewFormActions from "../InterviewFormActions";

const initialValueModal = {
  title: null,
  description: null,
  negativeBtn: null,
  positiveBnt: null,
  isOpen: false,
};

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const useStyle = makeStyles({
  logoImage: {
    objectFit: "contain",
  },
  editor: {
    height: 175,
  },
  dropzone: {
    height: 170,
  },
  previewButton: {
    padding: 0,
  },
  addButton: {
    fontSize: 40,
    marginRight: 15,
  },
  error: {
    fontSize: "0.75rem",
  },
  box: {
    overflow: "visible",
  },
});

function InterviewsInformation({
  mode = "edit",
  open,
  setOpen,
  selectedPanel = "",
  onPanelSelect = () => null,
  setPreviewContent,
  setInterviewPreview,
}) {
  const {
    currentEvent,
    setCurrentEvent,
    currentLoggedUser,
    venues,
    setVenues,
    userType,
    company,
    isCurrentFormDirty,
    setPanelChangesOpen,
    setChangePanel,
    isMobile,
    currentCompany,
    setCurrentCompany,
  } = useAppContextController();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyle();

  const [interviewTypesList, setInterviewTypesList] = useState();
  const [usersNames, setUsersNames] = useState();
  const [editorValue, setEditorValue] = useState(null);
  const [interviewToCopy, setInterviewToCopy] = useState(null);
  const [interviewLogo, setInterviewLogo] = useState("");
  const [copyInterviewName, setCopyInterviewName] = useState("");
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState("");

  const [confirmModal, setConfirmModal] = useState(initialValueModal);

  const closeModalHandler = () => setConfirmModal(initialValueModal);

  const [searchParams] = useSearchParams();

  const { createVenueSlug } = useParams();
  const copyInterviewId = searchParams.get("copyInterviewId");

  const { logoutAuth0User } = useSessionAuth();

  const { dropdowns: interviewTypes } = useDropdowns({ entity: INTERVIEW_TYPES });

  const isEdit = !!currentEvent?._id;

  const handlePanelChange = (panel) => {
    if (isCurrentFormDirty) {
      setPanelChangesOpen(true);
      setChangePanel(() => onPanelSelect(panel));
    } else {
      onPanelSelect(panel);
    }
  };

  const panelOptions = [
    {
      title: "Activities",
      icon: <Timeline />,
      hasShadow: selectedPanel === "activities" ? "emd" : "",
      color: "error",
      ariaLabel: "timeLine",
      fn: () => handlePanelChange("activities"),
      name: "activities",
    },
    {
      title: "Additional Information",
      icon: <Info />,
      hasShadow: selectedPanel === "additionalInfo" ? "emd" : "",
      color: "violet",
      ariaLabel: "information",
      fn: () => handlePanelChange("additionalInfo"),
      name: "additionalInfo",
    },
    {
      title: "Content",
      icon: <FolderCopy />,
      hasShadow: selectedPanel === "content" ? "emd" : "",
      color: "primary",
      ariaLabel: "content",
      fn: () => handlePanelChange("content"),
      name: "content",
    },
    {
      title: "Attendees",
      icon: <Diversity3 />,
      hasShadow: selectedPanel === "attendees" ? "emd" : "",
      color: "warning",
      ariaLabel: "attendees",
      fn: () => handlePanelChange("attendees"),
      name: "attendees",
    },
    {
      title: "Notes",
      icon: <Description />,
      hasShadow: selectedPanel === "notes" ? "emd" : "",
      color: "warning",
      ariaLabel: "notes",
      fn: () => handlePanelChange("notes"),
      name: "notes",
    },
    {
      title: "Attachments",
      icon: <AttachFile />,
      hasShadow: selectedPanel === "attachments" ? "emd" : "",
      color: "info",
      ariaLabel: "attachments",
      fn: () => handlePanelChange("attachments"),
      name: "attachments",
    },
  ];

  const { data: customFieldsResponse, isLoading: isLoadingFields } = useQuery(
    ["fetchCustomFieldsByFeature", "Interviews"],
    () => getCustomFieldByFeature("Interviews"),
    {
      enabled: !!currentEvent?.eventUrl,
      onError: (error) => {
        enqueueSnackbar(`Error loading custom fields: ${error.message}`, { variant: "error" });
      },
    }
  );

  const customFields = useMemo(
    () => customFieldsResponse?.data || [],
    [customFieldsResponse?.data]
  );

  const validationSchema = useMemo(() => {
    // const customFieldSchema = featureCustomFieldSchema(customFields);

    // return interviewInfoSchema.concat(customFieldSchema);
    return interviewInfoSchema;
  }, [customFields]);

  const options = { fetchAll: true };
  const allOptions = { ...options, filters: { primaryCompany: false } };
  const allOptionsUsers = { ...options, filters: { userType: "Master;Admin" } };
  const allOptionsJobs = {
    ...options,
    filters: {
      ...(selectedCustomerFilter ? { companySlug: selectedCustomerFilter.slug } : {}),
    },
  };

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const { data: users, isLoading: isLoadingUsers } = useQuery(["users", allOptionsUsers], () =>
    fetchUsers(allOptionsUsers)
  );

  const {
    data: companies,
    isLoading,
    isSuccess,
  } = useQuery(["companies", allOptions], () => fetchCustomers(allOptions), {
    refetchInterval: 300000,
  });

  const {
    data: jobs,
    isLoading: isLoadingJobs,
    refetch,
  } = useQuery(["availableJobs", allOptionsJobs], () => fetchCompanyJob(allOptionsJobs));

  const createInterviewMutation = useMutation(createEvent, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, data) => {
      await queryClient.invalidateQueries("events");
      enqueueSnackbar("Interview has been created!", { variant: "success" });
      setCurrentEvent({
        ...currentEvent,
        ...data,
        [_.id.name]: _.id?.[_.id.name],
        _id: _.result.insertedId,
      });
      navigate(`/events/${data.eventUrl}`);
    },
  });

  const updateInterviewMutation = useMutation(updateEvent, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, { data }) => {
      setCurrentEvent({
        ...currentEvent,
        ...data,
        eventId: currentEvent.eventId,
        eventUrl: currentEvent.eventUrl,
      });
      if (isEqual(data.status, "Deleted")) navigate("/events");
      enqueueSnackbar(
        `Interview has been ${isEqual(data.status, "Deleted") ? "archived" : "updated"}!`,
        { variant: "success" }
      );
      await queryClient.invalidateQueries("events");
    },
  });

  const resolver = useYupValidationResolver(validationSchema);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    watch,
    getValues,
    trigger,
    setError,
    formState,
  } = useForm({ mode: "onBlur", defaultValues: currentEvent, resolver });

  const selectedCompany = watch("companyName");

  const getCompanyField = (slug, field) => {
    if (!isLoading && isSuccess && companies && companies[slug]) {
      const { data } = companies;
      const companySlug = data.find((comp) => comp.slug === slug);
      return companySlug[field];
    }
    return null;
  };

  const createEventSuffix = () => {
    let suffix = "";
    if (copyInterviewName) suffix = ` from ${copyInterviewName}`;
    if (createVenueSlug)
      suffix = ` at ${
        getCompanyField(createVenueSlug, "name") ? getCompanyField(createVenueSlug, "name") : ""
      }`;
    return suffix;
  };

  const { errors, isSubmitting, dirtyFields, isDirty } = formState;

  const defaultValues = {
    eventName: `${watch("jobName") ?? company?.name} Interview`,
    jobName: "",
    jobId: "",
    eventType: "Interview",
    interviewType: "",
    status: "Active",
    _id: createVenueSlug ? getCompanyField(createVenueSlug, "_id") : null,
    companyName: createVenueSlug ? getCompanyField(createVenueSlug, "name") : null,
    interviewLocation: "",
    address: createVenueSlug ? getCompanyField(createVenueSlug, "address") : "",
    venueCity: createVenueSlug ? getCompanyField(createVenueSlug, "city") : "",
    venueState: createVenueSlug ? getCompanyField(createVenueSlug, "state") : "",
    zip: createVenueSlug ? getCompanyField(createVenueSlug, "zip") : "",
    eventId: "",
    eventDate: null,
    eventEndTime: null,
    reportTimeTBD: "",
    eventLogo: "",
    eventLogos: [],
    attachments: [],
    applicants: [],
    recruiter: "",
    hiringManager: "",
    // jobLogo: "No",
    // googleMap: "No",
    enableFeedback: "No",
    enableNotes: "No",
    companySlug: isEdit ? createVenueSlug || "" : "",
    description: "",
    eventUrl: "",
    attendees: [],
    logoUrl: "",
  };

  const openPreviewModal = (content) => {
    setPreviewContent(content);
    setInterviewPreview(currentEvent);
    setOpen(!open);
  };

  const setCustomer = async (slug) => {
    try {
      const customer = await fetchSingleCustomer({ slug });
      if (customer?.data) setCurrentCompany(customer?.data);
    } catch (error) {
      if (String(error).includes("401") || error?.response?.status === 401) {
        logoutAuth0User();
      }
    }
  };

  const onCompanyChange = () => {
    Object.keys(companies?.data).forEach((key) => {
      if (companies?.data[key]?.name.trim() === getValues("companyName").trim()) {
        setValue("logoUrl", companies?.data[key]?.logoUrl, { shouldDirty: true });
        setValue("companySlug", companies?.data[key]?.slug, { shouldDirty: true });
        setValue("address", companies?.data[key]?.address, { shouldDirty: true });
        setValue("companyName", companies?.data[key]?.name, { shouldDirty: true });
        setValue("venueCity", companies?.data[key]?.city, { shouldDirty: true });
        setValue("venueState", companies?.data[key]?.state, { shouldDirty: true });
        setValue("zip", companies?.data[key]?.zip, { shouldDirty: true });
      }
    });
  };

  const handleDateChange = (label, date) => {
    const newDate = new Date(date);
    const dateArr = date._a;

    if (dateArr) {
      const d = dateArr[2];
      const m = dateArr[1] + 1;
      const y = dateArr[0];
      const h = dateArr[3];
      const ms = dateArr[4];
      const lastDate = new Date(y, m, 0, h, ms);

      if (d > lastDate.getDate()) {
        setValue(label, lastDate, { shouldDirty: true });
      } else {
        setValue(label, newDate, { shouldDirty: true });
      }
    } else {
      setValue(label, newDate, { shouldDirty: true });
    }
  };

  const createInterviewFormHandler = async (values) => {
    const updatedValues = { ...values, eventName: `${values.jobName ?? company?.name} Interview` };
    if (copyInterviewId) updatedValues.copyInterviewId = copyInterviewId;
    updatedValues.createAgent = currentLoggedUser._id;
    updatedValues.eventEndTime = new Date(updatedValues.eventEndTime).toISOString();
    updatedValues.eventDate = new Date(updatedValues.eventDate).toISOString();

    await createInterviewMutation.mutateAsync(updatedValues);
  };

  const editInterviewFormHandler = async (values) => {
    if (copyInterviewId) return;
    if (!currentEvent?.companySlug || !currentEvent?.eventId) {
      enqueueSnackbar("Cannot Update Interview -- No Interview ID!", { variant: "error" });
      return;
    }
    const modifiedFields = getDirtyFields(values, dirtyFields);
    if (modifiedFields?.eventEndTime) {
      modifiedFields.eventEndTime = new Date(modifiedFields.eventEndTime).toISOString();
    }
    if (modifiedFields?.eventDate) {
      modifiedFields.eventDate = new Date(modifiedFields.eventDate).toISOString();
    }
    await updateInterviewMutation.mutateAsync({
      companySlug: currentEvent?.companySlug,
      eventId: currentEvent?._id,
      data: {
        ...modifiedFields,
      },
    });
  };

  useEffect(() => {
    if (copyInterviewId) delete currentEvent?._id;
    clearErrors();
    if (!isEdit) {
      const data = copyInterviewId && interviewToCopy ? interviewToCopy : defaultValues;
      reset(data);
    } else if (isEdit && currentEvent) {
      reset(currentEvent);
    }
    setSelectedCustomerFilter(null);

    if (currentEvent) setInterviewLogo(getValues("logoUrl"));
    setEditorValue(
      !copyInterviewId && !isEdit ? defaultValues?.description : currentEvent?.description
    );
  }, [isEdit, copyInterviewId, interviewToCopy, currentEvent]);

  useEffect(() => {
    setEditorValue(currentEvent?.description);
  }, [currentEvent]);

  useEffect(() => {
    const fetchInterviewToCopy = async (eid) => {
      const interview = await fetchSingleEvent({ eventId: eid });
      if (interview) {
        setCopyInterviewName(interview.eventName);
        interview.reportTime = "";
        interview.eventDate = "";
        interview.eventEndTime = "";
        interview.eventUrl = "";
        interview.eventId = "";
        interview.applicants = [];
        setValue("description", interview.description, { shouldDirty: true });
        delete interview._id;
        if (!interview.attendees) interview.attendees = [];
        if (!interview.attachments) interview.attachments = [];
        setInterviewToCopy(interview);
        setEditorValue(interview.description);
      }
    };

    if (copyInterviewId) {
      try {
        fetchInterviewToCopy(copyInterviewId);
      } catch (error) {
        if (String(error).includes("401") || error?.response?.status === 401) {
          logoutAuth0User();
        }
      }
    }
  }, [copyInterviewId]);

  const customerNames = useMemo(() => {
    if (companies?.data) {
      return companies?.data?.map((comp) => ({
        ...comp,
        label: `${comp.name}`,
      }));
    }
    return [];
  }, [companies?.data]);

  const jobNames = useMemo(() => {
    if (jobs?.data) {
      return jobs?.data?.map((job) => ({
        ...job,
        label: `${job.title} - ${job.companyName}`,
      }));
    }
    return [];
  }, [jobs?.data]);

  useEffect(() => {
    setInterviewTypesList(
      interviewTypes?.map((interview) => {
        if (interview === "Onsite {Primary Company}") {
          return interview.replace("{Primary Company}", `${company?.name}`);
        }
        if (interview === "Onsite {Customer}" && selectedCompany) {
          return interview.replace("{Customer}", `${selectedCompany}`);
        }
        if (interview === "Onsite {Customer}") {
          return interview.replace("{Customer}", "Customer");
        }
        return interview;
      })
    );
  }, [selectedCompany, company, interviewTypes]);

  useEffect(() => {
    if (users?.data) {
      setUsersNames(
        users?.data
          ?.filter((item) => {
            return ["Active"].includes(item.status) && ["Employee"].includes(item.spStatus);
          })
          .map((user) => ({
            userId: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            emailAddress: user.emailAddress,
            applicantId: user.applicantId,
          }))
      );
    }
  }, [users]);

  const dateToSlugString = (date) =>
    getValues()?.eventDate?.toString()?.split(" ")?.splice(0, 4)?.join("-")?.toLowerCase();

  useEffect(() => {
    if (isEdit) return;
    const hasEventUrl =
      watch("eventName") !== "" && watch("companySlug") !== "" && watch("eventDate") !== "";
    const variables = {
      venueSlug: watch("companySlug"),
      title: watch("eventName"),
      suffix: dateToSlugString(watch("eventDate")),
      slugField: "eventUrl",
      fetchFunction: fetchEvents,
    };

    if (hasEventUrl) {
      (async () => {
        const { slug } = await generateSlug(variables);
        setValue("eventUrl", slug, { shouldDirty: true });
      })();
    } else setValue("eventUrl", "", { shouldDirty: true });
  }, [watch("eventName"), watch("companySlug"), watch("eventDate")]);

  return (
    <CustomCard
      icon={<CompanyLogoSquare company={company} />}
      iconContainerSx={{ backgroundColor: "transparent" }}
      cardSx={{ width: "100%", overflow: "visible !important", mt: "20px", height: 850 }}
      cardTitle={
        isEdit
          ? `Interview Information: ${currentEvent?.eventName}`
          : `Create Interview ${createEventSuffix()}`
      }
      isStacked
    >
      <MDBox mt={!isMobile && -5}>
        <form
          onSubmit={
            isEdit
              ? handleSubmit(editInterviewFormHandler)
              : handleSubmit(createInterviewFormHandler)
          }
        >
          <FlexWrapper
            // maxWidth="100%"
            justifyContent="flex-end"
            // alignItems="center"
            // minHeight={38}
            mt={4}
            mb={3}
          >
            <InterviewFormActions
              submitted={isSubmitting}
              hasChanges={isDirty}
              resetChanges={() => reset(currentEvent)}
              setEditorValue={setEditorValue}
              copyInterviewId={copyInterviewId}
              updateInterviewMutation={updateInterviewMutation}
              onPreview={() => openPreviewModal("all")}
            />
            {/* <MDBox px={isEdit ? 0 : 3} pt={isEdit ? 0 : 1}>
              {!isEdit && (
                <>
                  <Button
                    variant="text"
                    startIcon={<CancelIcon />}
                    onClick={() => confirmModalHandler(isEdit ? "cancelModified" : "cancelCreate")}
                    disabled={isSubmitting}
                    style={{ color: "#F44335", fontSize: 14 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    endIcon={<SaveIcon />}
                    style={{ color: "white", fontSize: 14 }}
                    disabled={isSubmitting}
                  >
                    Create
                  </Button>
                </>
              )}
              {isEmpty(dirtyFields) && (
                <>
                  {isEdit && isEqual(userType, "Master") && (
                    <Button
                      variant="text"
                      startIcon={<DeleteIcon />}
                      onClick={deleteModalHandler}
                      style={{ color: "#F44335", fontSize: 14 }}
                      disabled={isSubmitting}
                    >
                      Delete Event
                    </Button>
                  )}
                  {isEdit && (
                    <Button
                      variant="text"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => navigate(`/events/create?copyEventId=${currentEvent?._id}`)}
                      disabled={isSubmitting}
                      style={{ fontSize: 14 }}
                    >
                      Copy Event
                    </Button>
                  )}
                </>
              )}
              {!isEmpty(dirtyFields) && (
                <>
                  {isEdit && isEqual(userType, "Master") && (
                    <Button
                      variant="text"
                      startIcon={<DeleteIcon />}
                      onClick={discardModalHandler}
                      style={{ color: "#F44335", fontSize: 14 }}
                      disabled={isSubmitting}
                    >
                      Discard
                    </Button>
                  )}
                  {isEdit && (
                    <Button
                      variant="text"
                      type="submit"
                      style={{ color: "#4CAF50", fontSize: 14 }}
                      startIcon={<SaveIcon />}
                      disabled={isSubmitting}
                    >
                      Save
                    </Button>
                  )}
                </>
              )}
            </MDBox> */}
          </FlexWrapper>

          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            sx={{ px: "1.5rem", mb: 2 }}
          >
            <Grid item xs={12} sm={6}>
              <Stack alignItems="flex-start">
                <Grid item xs={12}>
                  <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
                    Interview Information
                  </MDTypography>
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" justifyContent="flex-end">
                <Stack direction="row" alignItems="center" flexWrap="wrap">
                  <SplitPanelSelector
                    panelOptions={panelOptions}
                    selectedPanel={selectedPanel}
                    isEdit={isEdit}
                  />
                </Stack>
              </MDBox>
            </Grid>
          </Grid>

          {/* </FlexWrapper> */}
          <StyledContainer px={3} pb={3}>
            <Grid container spacing={3} mt={-1}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="jobName"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={jobNames || []}
                      freeSolo
                      disabled={isEdit}
                      loading={isLoadingJobs}
                      defaultValue=""
                      value={field.value || ""}
                      onChange={(e, v) => {
                        setValue("jobName", v.title, { shouldDirty: true });
                        setValue("jobId", v.jobId, { shouldDirty: true });
                        setValue("jobSlug", v.jobSlug, { shouldDirty: true });
                        setValue("companyName", v.companyName, { shouldDirty: true });
                        setValue("companySlug", v.companySlug, { shouldDirty: true });
                        setValue("hiringManager", "", { shouldDirty: true });
                        setValue("interviewType", "", { shouldDirty: true });
                        setValue("interviewLocation", "", { shouldDirty: true });
                        setCustomer(v.companySlug);
                      }}
                      renderOption={(props, option, index) => {
                        const key = `listItem-${index}-${option._id}`;
                        return (
                          <li {...props} key={key}>
                            {option.label}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          key={`jobName_${currentEvent?._id}`}
                          type="text"
                          label="Job Name"
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventId"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      disabled
                      InputLabelProps={{ shrink: !isEmpty(getValues("eventId")), tabIndex: -1 }}
                      value={currentEvent?.eventId || ""}
                      key={`_id${currentEvent?.eventId}`}
                      type="text"
                      label=" Interview ID"
                      {...field}
                    />
                  )}
                />
              </Grid>
              {isEdit ? (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="companyName"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        autoSelect
                        disabled={isEdit}
                        options={/* companyNames || */ []}
                        name="companyName"
                        value={field.value || null}
                        onChange={(e) => {
                          field.onChange(e.target.textContent);
                          onCompanyChange();
                          trigger("address");
                        }}
                        renderInput={(params) => (
                          <FormField
                            key={`companyName_${currentEvent?._id}`}
                            {...params}
                            type="text"
                            label="Customer Name"
                            InputLabelProps={{ tabIndex: -1 }}
                          />
                        )}
                      />
                    )}
                  />
                  {errors?.companyName && (
                    <MDTypography className={classes.error} color="error">
                      {errors?.companyName.message}
                    </MDTypography>
                  )}
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={customerNames || []}
                    freeSolo
                    defaultValue=""
                    loading={isLoading}
                    value={selectedCustomerFilter?.name || ""}
                    onChange={(e, v) => {
                      setSelectedCustomerFilter(v);
                    }}
                    renderOption={(props, option, index) => {
                      const key = `listItem-${index}-${option._id}`;
                      return (
                        <li {...props} key={key}>
                          {option.label}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <FormField
                        {...params}
                        type="text"
                        label="Filter Job by Customer"
                        InputLabelProps={{ tabIndex: -1 }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="jobId"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomTextField
                      disabled
                      key={`jobId_${currentEvent?.companySlug}_${currentEvent?.jobId}`}
                      type="text"
                      label="Job ID"
                      InputLabelProps={{ shrink: !isEmpty(getValues("jobId")), tabIndex: -1 }}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="interviewType"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      key={`autocomplete_interviewType_${currentEvent?._id}`}
                      options={interviewTypesList || []}
                      autoSelect
                      freeSolo
                      disabled={isEmpty(watch("jobName"))}
                      name="Interview Type"
                      defaultValue={currentEvent?.interviewType || null}
                      value={field.value || ""}
                      onChange={(e, v) => {
                        field.onChange(v?.charAt(0).toUpperCase() + v?.slice(1));
                        setValue("interviewLocation", "", { shouldDirty: true });
                      }}
                      onBlur={() => trigger("interviewType")}
                      renderInput={(params) => (
                        <FormField
                          key={`interviewType_${currentEvent?._id}`}
                          {...params}
                          type="text"
                          label="Interview Type"
                        />
                      )}
                    />
                  )}
                />
                {errors?.interviewType && (
                  <MDTypography className={classes.error} color="error">
                    {errors?.interviewType.message}
                  </MDTypography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="interviewLocation"
                  control={control}
                  render={({ field }) => (
                    <SelectInterviewLocation
                      disabled={isEmpty(getValues("jobName"))}
                      inputProps={isEmpty(getValues("companyName")) ? { tabIndex: -1 } : {}}
                      value={field.value || ""}
                      onChange={(e, v) => {
                        field.onChange(v);
                      }}
                      isOptionEqualToValue={(option, value) => option?.label === value?.label}
                      currentEventId={currentEvent?._id}
                      company={company}
                      currentCompany={currentCompany}
                      selectedCompany={selectedCompany}
                      selectedInterviewType={watch("interviewType")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="recruiter"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      key={`autocomplete_recruiter_${currentEvent?.id}`}
                      options={
                        usersNames?.sort((a, b) => (a.firstName > b.firstName ? 1 : -1)) || []
                      }
                      autoSelect
                      name="recruiter"
                      defaultValue={currentEvent?.recruiter}
                      value={field.value || ""}
                      onChange={(e, v) => {
                        setValue("recruiter", v, { shouldDirty: true });
                      }}
                      onBlur={() => trigger("recruiter")}
                      getOptionLabel={(option) => {
                        return option ? `${option.firstName} ${option.lastName}` : "";
                      }}
                      blurOnSelect
                      isOptionEqualToValue={(option, value) => {
                        return option.userId === value.userId;
                      }}
                      renderInput={(params) => (
                        <>
                          <FormField
                            key={`recruiter_${currentEvent?.id}`}
                            {...params}
                            type="text"
                            label="Recruiter"
                            // InputLabelProps={{ shrink: !isEmpty(getValues("recruiter")) }}
                          />
                          <FormHelperText error>{error?.message}</FormHelperText>
                        </>
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="hiringManager"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <SelectHiringManager
                      customer={currentCompany}
                      currentEvent={currentEvent}
                      value={field.value || ""}
                      onChange={(e, v) => {
                        field.onChange(v);
                      }}
                      onBlur={() => trigger("hiringManager")}
                      errorMessage={error?.message}
                      isShrink={!isEmpty(getValues("hiringManager"))}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventDate"
                  control={control}
                  defaultValue={currentEvent?.eventDate}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        disabled={isEmpty(watch("companyName"))}
                        inputProps={isEmpty(watch("companyName")) ? { tabIndex: -1 } : {}}
                        label="Start Time"
                        value={!field.value ? null : field.value}
                        onChange={(date) => {
                          clearErrors("eventEndTime");
                          handleDateChange("eventDate", date);
                          setValue(
                            "eventEndTime",
                            new Date(
                              new Date(getValues().eventDate).getTime() + 1 * 60 * 60 * 1000
                            ),
                            { shouldDirty: true }
                          );
                          clearErrors("eventDate");
                        }}
                        renderInput={(params) => <FormField {...params} />}
                      />
                    </LocalizationProvider>
                  )}
                />
                {errors?.eventDate && (
                  <MDTypography className={classes.error} color="error">
                    {errors?.eventDate.message}
                  </MDTypography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventEndTime"
                  control={control}
                  defaultValue={currentEvent?.eventEndTime}
                  render={({ field }) => {
                    return (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          disabled={isEmpty(watch("companyName"))}
                          inputProps={isEmpty(watch("companyName")) ? { tabIndex: -1 } : {}}
                          label="End Time"
                          value={!field.value ? null : field.value}
                          onChange={(date) => {
                            handleDateChange("eventEndTime", date);
                          }}
                          renderInput={(params) => <FormField {...params} />}
                        />
                      </LocalizationProvider>
                    );
                  }}
                />
                {errors?.eventEndTime && (
                  <MDTypography className={classes.error} color="error">
                    {errors?.eventEndTime.message}
                  </MDTypography>
                )}
              </Grid>

              <Grid container item xs={12}>
                <Grid item flexBasis={0} flexGrow={1} flexShrink={1}>
                  <Controller
                    name="eventUrl"
                    control={control}
                    render={({ field }) => (
                      <FormField
                        key={`eventUrl_${currentEvent?._id}`}
                        disabled={
                          isEdit ||
                          !getValues()?.companySlug?.length ||
                          !getValues()?.interviewDate ||
                          !getValues()?.name?.length
                        }
                        type="text"
                        value={field.value || ""}
                        label=" Interview URL"
                        InputLabelProps={{
                          shrink: !isEmpty(getValues("eventUrl")),
                          tabIndex:
                            isEdit ||
                            !getValues()?.companySlug?.length ||
                            !getValues()?.interviewDate ||
                            !getValues()?.name?.length
                              ? -1
                              : undefined,
                        }}
                        {...field}
                      />
                    )}
                  />
                  {errors?.eventUrl && (
                    <MDTypography className={classes.error} color="error">
                      {errors?.eventUrl?.message}
                    </MDTypography>
                  )}
                </Grid>
                <Grid item flexBasis={1} flexGrow={0} flexShrink={0}>
                  <Tooltip title="Copy Url">
                    <IconButton
                      color="info"
                      onClick={() => {
                        navigator.clipboard.writeText(getValues().eventUrl);
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
              {watch("interviewType") === "Online" && (
                <Grid item xs={12}>
                  <Controller
                    name="interviewLink"
                    control={control}
                    render={({ field }) => (
                      <FormField
                        disabled={watch("interviewType") !== "Online"}
                        inputProps={watch("interviewType") !== "Online" ? { tabIndex: -1 } : {}}
                        type="text"
                        label="Interview Link"
                        InputLabelProps={{ shrink: !isEmpty(getValues("interviewLink")) }}
                        {...field}
                        value={field.value ?? ""}
                      />
                    )}
                  />
                </Grid>
              )}
              {isEdit && (
                <Grid container item xs={12} justifyContent="space-between">
                  <MDBox>
                    <Button
                      startIcon={<Work />}
                      sx={{
                        "& svg": {
                          fontSize: "1.15rem !important",
                        },
                        padding: "0",
                        textTransform: "none",
                        fontSize: "1.15rem !important",
                      }}
                      onClick={() =>
                        navigate(`/companyjobs/${currentEvent?.jobSlug}/action/jobinfo`)
                      }
                    >
                      Go to Job
                    </Button>
                  </MDBox>
                  <MDBox>
                    <AddToCalendarButton event={currentEvent} />
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </StyledContainer>
        </form>
      </MDBox>
    </CustomCard>
  );
}

export default InterviewsInformation;

// Setting default values for the props of BaseLayout
InterviewsInformation.defaultProps = {};

// Typechecking props for BaseLayout
InterviewsInformation.propTypes = {};

const StyledContainer = styled(MDBox)(() => ({
  height: 660,
  overflowY: "auto",
}));
